const DEFAULT_IMGIX_PARAMETERS = {
  auto: 'format,compress',
  fit: 'max',
};

// Imgix can rasterize image-oriented source formats such as PDF, PSD, and TIF.
// Block only extensions that are known to be video, audio, captions, data, or
// archives so an accidental call cannot turn those objects into Imgix requests.
const NON_IMAGE_EXTENSIONS = new Set([
  'aac',
  'avi',
  'html',
  'json',
  'm3u8',
  'm4a',
  'm4v',
  'mkv',
  'mov',
  'mp3',
  'mp4',
  'mpd',
  'srt',
  'tar',
  'txt',
  'vtt',
  'wav',
  'webm',
  'xml',
  'zip',
]);

const withImgixAutoDefaults = value => {
  const values = (value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

  ['format', 'compress'].forEach(defaultValue => {
    if (!values.some(valueItem => valueItem.toLowerCase() === defaultValue)) {
      values.push(defaultValue);
    }
  });

  return values.join(',');
};

const decodeQueryComponent = value => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch (error) {
    return value;
  }
};

const isImgixHost = url => {
  const match = url.match(/^(?:https?:)?\/\/([^/?#]+)/i);
  if (!match) {
    return false;
  }

  const hostname = match[1]
    .split('@')
    .pop()
    .split(':')[0]
    .toLowerCase();

  return hostname === 'imgix.net' || hostname.endsWith('.imgix.net');
};

const hasKnownNonImageExtension = url => {
  const pathname = url.split(/[?#]/, 1)[0];
  const match = pathname.match(/\.([a-z0-9]+)$/i);
  return match ? NON_IMAGE_EXTENSIONS.has(match[1].toLowerCase()) : false;
};

const parseQuery = query => {
  const pairs = [];

  query
    .replace(/^\?/, '')
    .split('&')
    .forEach(pair => {
      if (!pair) {
        return;
      }
      const separatorIndex = pair.indexOf('=');
      const rawKey = separatorIndex >= 0 ? pair.slice(0, separatorIndex) : pair;
      const rawValue =
        separatorIndex >= 0 ? pair.slice(separatorIndex + 1) : '';
      pairs.push([
        decodeQueryComponent(rawKey),
        decodeQueryComponent(rawValue),
      ]);
    });

  return pairs;
};

export const canOptimizeImgixUrl = url =>
  !!url && isImgixHost(url) && !hasKnownNonImageExtension(url);

export const imgixUrl = (url, style = {}) => {
  if (!url) {
    return '';
  }

  if (!canOptimizeImgixUrl(url)) {
    return url;
  }

  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl =
    queryIndex >= 0 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash;
  const existingQuery =
    queryIndex >= 0 ? urlWithoutHash.slice(queryIndex + 1) : '';
  const parameters = Object.create(null);
  const parameterOrder = [];

  const setParameter = (key, value, overwrite = true) => {
    if (!overwrite && Object.prototype.hasOwnProperty.call(parameters, key)) {
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(parameters, key)) {
      parameterOrder.push(key);
    }
    parameters[key] = value;
  };

  parseQuery(existingQuery).forEach(([key, value]) => {
    setParameter(key, value);
  });

  // Existing signatures cover the complete query string. Client-side changes
  // would invalidate them, so signed URLs must be returned byte-for-byte.
  if (Object.prototype.hasOwnProperty.call(parameters, 's')) {
    return url;
  }

  if (Object.prototype.hasOwnProperty.call(parameters, 'auto')) {
    parameters.auto = withImgixAutoDefaults(parameters.auto);
  }

  Object.keys(DEFAULT_IMGIX_PARAMETERS).forEach(key => {
    setParameter(key, DEFAULT_IMGIX_PARAMETERS[key], false);
  });
  const styleEntries =
    typeof style === 'string' ? parseQuery(style) : Object.entries(style || {});
  styleEntries.forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      setParameter(key, value);
    }
  });

  const query = parameterOrder
    .map(
      key =>
        `${encodeURIComponent(key)}=${encodeURIComponent(parameters[key])}`,
    )
    .join('&');

  return `${baseUrl}?${query}${hash}`;
};

export default imgixUrl;
